import { Button, Card, Switch, Text } from "@ttc3k/trekker";
import { type ReactNode, useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { cva } from "styled-system/css";
import { Box, Flex, styled } from "styled-system/jsx";
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import {
  EnumProductSellingConfigRecurringMultiListingMode as MultiListingMode,
  useRenewableFeeCoveredOperatorsQuery,
} from "gql/generated";
import type { IncludedBusiness } from "widgets/booker/contexts/CartContextProvider/cartContextTypes";
import { useBookerContext, useCartContext } from "widgets/booker/contexts";
import type { EarlyRenewalEntry } from "widgets/booker/contexts/CartContextProvider/CartContext";
import { getEarlyRenewalDates } from "widgets/booker/shared/utils";
import { formatPrice } from "shared/utils";

const businessCardSurfaceStyles = cva({
  base: {
    width: "full",
    outline: "0 transparent solid",
    transitionDuration: "normal",
    transitionProperty: "border, outline",
    transitionTimingFunction: "default",
    boxShadow: "none",
  },
  variants: {
    visual: {
      unChecked: {},
      checked: {
        borderColor: "border.dark",
        outlineWidth: "1px",
        outlineColor: "border.dark",
      },
    },
  },
  defaultVariants: {
    visual: "unChecked",
  },
});

const BusinessCardSurface = styled(Card.Surface, businessCardSurfaceStyles);

/** Inline copy of `/icons/grabber.svg`; a root-relative URL breaks when the booker is embedded. */
const GrabberIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    {[6, 12, 18].flatMap(cy =>
      [8, 16].map(cx => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="2" fill="#D4D4D8" />
      )),
    )}
  </svg>
);

type BusinessCardProps = {
  name: string;
  subText: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
  dragHandle?: ReactNode;
};

const BusinessCard = ({
  name,
  subText,
  checked,
  disabled,
  onCheckedChange,
  dragHandle,
}: BusinessCardProps) => (
  <BusinessCardSurface
    visual={checked ? "checked" : "unChecked"}
    opacity={disabled ? 0.5 : 1}
  >
    <Card.Body bg="white" py="250">
      <Card.BodyContent alignItems="center" gap="150">
        {dragHandle}
        <Box flex="1" minW="0">
          <Card.Title mainText={name} subText={subText} />
        </Box>
        <Switch
          checked={checked}
          disabled={disabled}
          aria-label={name}
          onCheckedChange={e => onCheckedChange(e.checked)}
        />
      </Card.BodyContent>
    </Card.Body>
  </BusinessCardSurface>
);

type SortableBusinessCardProps = Omit<BusinessCardProps, "dragHandle"> & {
  id: string;
  sortable: boolean;
  dragLabel: string;
};

const SortableBusinessCard = ({
  id,
  sortable,
  dragLabel,
  ...cardProps
}: SortableBusinessCardProps) => {
  const {
    setNodeRef,
    setActivatorNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !sortable });

  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      position="relative"
      zIndex={isDragging ? 1 : undefined}
    >
      <BusinessCard
        {...cardProps}
        dragHandle={
          sortable ? (
            <Box
              ref={setActivatorNodeRef}
              {...attributes}
              {...listeners}
              aria-label={dragLabel}
              cursor={isDragging ? "grabbing" : "grab"}
              touchAction="none"
              display="flex"
              color="icon.light"
              flexShrink={0}
            >
              <GrabberIcon />
            </Box>
          ) : undefined
        }
      />
    </Box>
  );
};

type MultiBusinessSelectorProps = {
  operators: { entityId: string; name: string }[];
  organizationId?: string | null;
  mode: MultiListingMode;
  maxListings?: number | null;
  value: IncludedBusiness[];
  onChange: (next: IncludedBusiness[]) => void;
  /** Server-priced amount per selected position, when a quote is available. */
  lineAmounts?: Map<number, number>;
  /** Businesses to preselect (e.g. the ones renewing); wins over the all-included prefill. */
  initialOperatorIds?: string[];
  /** Not a membership: businesses are only associated with the purchase, never priced or covered. */
  associateOnly?: boolean;
  allowSelectAll?: boolean;
  /** Checkout email; coverage is "yours" only when it's under this person. */
  customerEmail?: string;
};

export const MultiBusinessSelector = ({
  operators,
  organizationId,
  mode,
  maxListings,
  value,
  onChange,
  lineAmounts,
  initialOperatorIds,
  associateOnly = false,
  allowSelectAll = false,
  customerEmail,
}: MultiBusinessSelectorProps) => {
  const { t } = useTranslation("booker", {
    keyPrefix: "CUSTOMER_DETAILS.FORM",
  });

  const operatorIds = useMemo(
    () => operators.map(op => op.entityId),
    [operators],
  );
  const { data: coveredData, loading: coveredLoading } =
    useRenewableFeeCoveredOperatorsQuery({
      variables: {
        organizationId: organizationId ?? "",
        operatorIds,
        ...(customerEmail ? { customerEmail } : {}),
      },
      skip: associateOnly || !organizationId || operatorIds.length === 0,
    });
  const coverage = useMemo(() => {
    const map = new Map<string, { coveredByYou: boolean }>();
    for (const c of coveredData?.renewableFeeCoveredOperators ?? []) {
      map.set(c.operatorId, { coveredByYou: c.coveredByYou });
    }
    return map;
  }, [coveredData]);

  const { earlyRenewals } = useCartContext();
  const { language } = useBookerContext();
  const paidUp = useMemo(() => {
    const map = new Map<string, EarlyRenewalEntry>();
    if (associateOnly) return map;
    for (const entry of earlyRenewals.entries) {
      if (entry.operatorId) map.set(entry.operatorId, entry);
    }
    return map;
  }, [associateOnly, earlyRenewals.entries]);
  const availabilityLoading = coveredLoading || earlyRenewals.loading;

  const limit = maxListings && maxListings > 1 ? maxListings : Infinity;
  const selectedIds = new Set(value.map(b => b.operatorEntityId));
  const atLimit = value.length >= limit;

  // Covered by someone else's membership, or already paid up and not yet
  // open for renewal: checkout would reject either.
  const isAvailable = useCallback(
    (entityId: string) => {
      const c = coverage.get(entityId);
      return (!c || c.coveredByYou) && !paidUp.has(entityId);
    },
    [coverage, paidUp],
  );

  // A business already picked can turn out to be unavailable once the
  // checkout email is known.
  useEffect(() => {
    if (availabilityLoading) return;
    const kept = value.filter(b => isAvailable(b.operatorEntityId));
    if (kept.length !== value.length) onChange(kept);
  }, [isAvailable, availabilityLoading, value, onChange]);

  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || availabilityLoading) return;
    prefilled.current = true;
    if (value.length > 0) return;
    if (initialOperatorIds?.length) {
      const initial = initialOperatorIds
        .map(id => operators.find(op => op.entityId === id))
        .filter((op): op is (typeof operators)[number] => !!op)
        .filter(op => isAvailable(op.entityId))
        .slice(0, limit === Infinity ? undefined : limit)
        .map(op => ({ operatorEntityId: op.entityId, businessName: op.name }));
      if (initial.length > 0) {
        onChange(initial);
        return;
      }
    }
    if (mode !== MultiListingMode.AllIncluded) return;
    const all = operators
      .filter(op => isAvailable(op.entityId))
      .slice(0, limit === Infinity ? undefined : limit)
      .map(op => ({ operatorEntityId: op.entityId, businessName: op.name }));
    if (all.length > 0) onChange(all);
  }, [
    availabilityLoading,
    mode,
    operators,
    isAvailable,
    limit,
    value.length,
    onChange,
    initialOperatorIds,
  ]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const oldIndex = value.findIndex(b => b.operatorEntityId === active.id);
    const newIndex = value.findIndex(b => b.operatorEntityId === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onChange(arrayMove(value, oldIndex, newIndex));
  };

  const selectedSubText = (business: IncludedBusiness, index: number) => {
    const parts: string[] = [];
    if (index === 0) parts.push(t("PRIMARY"));
    const amount = lineAmounts?.get(index);
    if (amount != null) {
      parts.push(
        amount === 0 && mode === MultiListingMode.AllIncluded
          ? t("INCLUDED")
          : `$${formatPrice(amount)}`,
      );
    }
    if (coverage.get(business.operatorEntityId)?.coveredByYou) {
      parts.push(t("COVERED_BY_YOU"));
    }
    return parts.join(" · ");
  };

  const unselected = operators.filter(op => !selectedIds.has(op.entityId));
  const canReorder = value.length > 1;
  const selectable = unselected.filter(op => isAvailable(op.entityId));
  const selectAll = () =>
    onChange(
      [
        ...value,
        ...selectable.map(op => ({
          operatorEntityId: op.entityId,
          businessName: op.name,
        })),
      ].slice(0, limit === Infinity ? undefined : limit),
    );

  return (
    <Flex flexDir="column" gap="100">
      <Flex justifyContent="space-between" alignItems="center" gap="100">
        <Text visual="smallMedium" color="text.dark">
          {t("BUSINESSES")}
        </Text>
        {allowSelectAll && selectable.length > 0 && !atLimit && (
          <Button size="sm" mode="light" type="button" onClick={selectAll}>
            {t("SELECT_ALL")}
          </Button>
        )}
      </Flex>
      <Text visual="smallRegular" color="text.mid">
        {associateOnly
          ? canReorder
            ? t("BUSINESSES_HINT_ASSOCIATE_DRAG")
            : t("BUSINESSES_HINT_ASSOCIATE")
          : canReorder
            ? t("BUSINESSES_HINT_DRAG")
            : t("BUSINESSES_HINT")}
      </Text>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={value.map(b => b.operatorEntityId)}
          strategy={verticalListSortingStrategy}
        >
          <Flex flexDir="column" gap="100">
            {value.map((business, index) => (
              <SortableBusinessCard
                key={business.operatorEntityId}
                id={business.operatorEntityId}
                sortable={canReorder}
                dragLabel={t("DRAG_TO_REORDER")}
                name={business.businessName ?? ""}
                subText={selectedSubText(business, index)}
                checked
                onCheckedChange={() =>
                  onChange(
                    value.filter(
                      b => b.operatorEntityId !== business.operatorEntityId,
                    ),
                  )
                }
              />
            ))}
          </Flex>
        </SortableContext>
      </DndContext>

      {unselected.map(op => {
        const c = coverage.get(op.entityId);
        const coveredByOther = !!c && !c.coveredByYou;
        const paidUpEntry = paidUp.get(op.entityId);
        return (
          <BusinessCard
            key={op.entityId}
            name={op.name}
            subText={
              coveredByOther
                ? t("COVERED_BY_OTHER")
                : paidUpEntry
                  ? t("PAID_UP", getEarlyRenewalDates(paidUpEntry, language))
                  : c?.coveredByYou
                    ? t("COVERED_BY_YOU")
                    : ""
            }
            checked={false}
            disabled={coveredByOther || !!paidUpEntry || atLimit}
            onCheckedChange={() =>
              onChange([
                ...value,
                { operatorEntityId: op.entityId, businessName: op.name },
              ])
            }
          />
        );
      })}

      {atLimit && unselected.length > 0 && limit !== Infinity && (
        <Text visual="smallRegular" color="text.mid">
          {t("MAX_REACHED", { count: limit })}
        </Text>
      )}
    </Flex>
  );
};
